import { nanoid } from 'nanoid'
import {
  buildPublicComposerDraft,
  clearPublicComposerDraftStorage,
  createPublicComposerFilesKey,
  deletePublicComposerDraftFiles,
  readPublicComposerDraft,
  writePublicComposerDraft,
  writePublicComposerDraftFiles,
} from '@/components/chat/public-composer-draft'

export const AUDITED_EMAIL_FILE_NAME = 'audited-email.html'

/** Marks the drafts this handoff writes; nothing else sets it. */
const FREE_EMAIL_AUDIT_DRAFT_ORIGIN = 'email-audit'

/**
 * Hand the audited email to Brew through the public composer draft: the HTML
 * as an attachment (IndexedDB) and the prompt as the draft text
 * (localStorage). After sign-in both land in a chat composer, so the visitor
 * arrives with their email ready to import: the `/home` launcher for an
 * existing account, the chat onboarding opens for a new one
 * (`components/brand-setup/setup-chat-public-draft.ts`).
 *
 * Resolves `false`, writing no draft, when the browser keeps neither (no
 * IndexedDB in a private window, blocked or full storage): the caller then
 * keeps the email on the page instead of signing in without it.
 */
export async function saveAuditedEmailForBrew({
  html,
  prompt,
}: {
  html: string
  prompt: string
}): Promise<boolean> {
  if (typeof indexedDB === 'undefined') {
    return false
  }
  const chatId = nanoid()
  const fileId = nanoid()
  const file = new File([html], AUDITED_EMAIL_FILE_NAME, { type: 'text/html' })
  const filesKey = createPublicComposerFilesKey(chatId)
  // The draft is one slot: once this one replaces it, the previous draft's
  // files have nothing pointing at them, so they go once the save succeeds.
  const previousFilesKey = readPreviousFilesKey()
  try {
    await writePublicComposerDraftFiles(filesKey, [
      { id: fileId, file, isHtmlAttachment: true },
    ])
    writePublicComposerDraft({
      ...buildPublicComposerDraft({
        chatId,
        text: prompt,
        filesKey,
        files: [
          {
            id: fileId,
            filename: file.name,
            mediaType: file.type,
            isHtmlAttachment: true,
          },
        ],
      }),
      origin: FREE_EMAIL_AUDIT_DRAFT_ORIGIN,
    })
  } catch {
    // No draft points at the file once the draft write fails, so nothing
    // would ever clear it: drop it here (a no-op when the file write failed).
    await deletePublicComposerDraftFiles(filesKey)
    return false
  }
  if (previousFilesKey && previousFilesKey !== filesKey) {
    await deletePublicComposerDraftFiles(previousFilesKey)
  }
  return true
}

function readPreviousFilesKey(): string | undefined {
  try {
    return readPublicComposerDraft()?.filesKey
  } catch {
    return undefined
  }
}

/**
 * Drop an audited email an earlier handoff left for Brew, so signing in
 * without the current email never restores an older one. Only a draft this
 * handoff marked is cleared: one written anywhere else stays, even if it
 * carries a downloaded `audited-email.html`.
 */
export async function discardAuditedEmailForBrew(): Promise<void> {
  try {
    const draft = readPublicComposerDraft()
    if (draft?.origin === FREE_EMAIL_AUDIT_DRAFT_ORIGIN) {
      await clearPublicComposerDraftStorage(draft.filesKey)
    }
  } catch {
    // Storage refused: there is nothing this browser kept to clear.
  }
}

/** Save the audited email as a file, for a browser that refused storage. */
export function downloadAuditedEmail(html: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
  const link = document.createElement('a')
  link.href = url
  link.download = AUDITED_EMAIL_FILE_NAME
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
